import { cn } from '@/lib/utils';
import { Control, Controller, Path } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import { useMemo, useState } from 'react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { countries } from '@/components/everridge/countries';

type Props<T extends {}> = {
	control: Control<T, any>;
	fieldName: Path<T>;
	label?: string;
	iddPlaceholder?: string;
	numberPlaceholder?: string;
	containerClassName?: string;
	disabled?: boolean;
};

const EPhoneNumberInput = <T extends {}>({
	label,
	iddPlaceholder = 'Select IDD',
	numberPlaceholder = 'Enter number',
	fieldName,
	control,
	containerClassName,
	disabled,
}: Props<T>) => {
	const [isOpenedIddList, setIsOpenedIddList] = useState(false);

	const toggleIddDropdown = () => setIsOpenedIddList((prev) => !prev);

	const phoneIddList = useMemo(() => {
		return countries
			.map((country) => {
				const iddList = country.idd?.split(',') ?? [country.idd];
				return iddList.map((idd) => ({
					number: idd.replace(/\D/g, ''),
					flag: country.flag,
				}));
			})
			.flat()
			.sort((a, b) => Number(a.number) - Number(b.number));
	}, []);

	return (
		<div
			className={cn(
				'w-full flex flex-col gap-1.5 select-none',
				containerClassName,
			)}>
			<h4 className='text-sm font-medium'>{label}</h4>
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange, onBlur },
					fieldState,
					formState,
				}) => {
					const longestMatch = useMemo(() => {
						const stringValue = value ?? '';

						return phoneIddList
							.filter(
								(idd) =>
									idd.number &&
									stringValue.startsWith(idd.number),
							)
							.reduce(
								(prev, idd) =>
									idd.number.length > prev.number.length
										? idd
										: prev,
								{ number: '', flag: () => null },
							);
					}, [value]);

					const matchedIdd = longestMatch;
					const phoneNumber = value
						? String(value).slice(matchedIdd.number.length)
						: '';

					const handleChange = (
						newIdd: string,
						newNumber: string,
					) => {
						onChange(`${newIdd}${newNumber}`);
					};

					return (
						<>
							<div className='flex'>
								<DropdownMenu
									open={isOpenedIddList}
									onOpenChange={toggleIddDropdown}>
									<DropdownMenuTrigger asChild>
										<button
											className={cn(
												'w-50 h-[46px] p-3.5 flex items-center gap-2.5 border border-r-0 border-popover-100 rounded-l-semi-xl text-xs text-start bg-popover-10 truncate outline-hidden focus:border focus:border-primary-color-1000 disabled:bg-popover-50 transition-colors duration-150 cursor-pointer',
												fieldState.invalid
													? 'border-error-1000'
													: '',
											)}
											disabled={
												disabled || formState.disabled
											}
											onClick={toggleIddDropdown}>
											{matchedIdd.number ? (
												<div className='flex gap-2'>
													{matchedIdd.flag()}
													<span className='text-popover-800'>{`+ ${matchedIdd.number}`}</span>
												</div>
											) : (
												<span className='text-popover-600'>
													{iddPlaceholder}
												</span>
											)}
											<div
												className={cn(
													'transition-transform duration-50 ease-in-out ml-auto',
													isOpenedIddList
														? '-rotate-180'
														: 'rotate-0',
												)}>
												{<ArrowDownIcon />}
											</div>
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										className={
											'w-(--radix-dropdown-menu-trigger-width) max-h-[242px] p-0 rounded-semi-xl border border-popover-100 bg-popover-10 overflow-y-scroll no-scrollbar'
										}>
										{phoneIddList.map((idd, index) => (
											<DropdownMenuItem
												key={index}
												onClick={() =>
													handleChange(
														idd.number,
														phoneNumber,
													)
												}>
												<div className='flex gap-2.5'>
													<span>{idd.flag()}</span>
													<span className='text-xs text-popover-800 truncate'>
														{`+ ${idd.number}`}
													</span>
												</div>
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
								<div className='my-3.5 border-r border-popover-100' />
								<input
									value={phoneNumber}
									onChange={(e) =>
										handleChange(
											matchedIdd.number,
											e.target.value,
										)
									}
									onBlur={onBlur}
									disabled={disabled || formState.disabled}
									placeholder={numberPlaceholder}
									className={cn(
										'w-full p-3.5 border border-l-0 border-popover-100 rounded-r-semi-xl text-xs text-popover-800 bg-popover-10 focus:outline-hidden focus:border focus:border-primary-color-1000 placeholder:text-popover-600 disabled:bg-popover-50 transition-colors duration-150',
										fieldState.invalid
											? 'border-error-1000'
											: '',
									)}
								/>
							</div>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{fieldState.error?.message}
									</span>
								</div>
							)}
						</>
					);
				}}
			/>
		</div>
	);
};

export default EPhoneNumberInput;
